import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import config from "../../config/catalogosConfig";
import Modal from "../Modal";
import {
  FaSearch, FaDownload, FaPlus, FaPencilAlt, FaBan, FaCheck, FaChevronLeft, FaChevronRight,
  FaExclamationTriangle,
} from "react-icons/fa";

export default function CatalogoTable({ tabla, onBack, nombre, nombreSingular, descripcion, icono: Icono, filtroCampo, filtroValor, valoresFijos = {}, nombreSeccion = "Catálogos" }) {
  const { token } = useContext(AuthContext);

  const [data, setData] = useState([]);
  const [refsData, setRefsData] = useState({});
  const [search, setSearch] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("todos");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [editando, setEditando] = useState(false);
  const [opcionesCampo, setOpcionesCampo] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [accionTipo, setAccionTipo] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const limit = 10;
  const cfg = config[tabla];
  const totalPages = Math.ceil(total / limit);

  // =========================
  // CARGAR DATOS
  // =========================
  const cargarDatos = async () => {
    if (!cfg) return;
    try {
      setLoading(true);
      const filtroExtra = filtroCampo && filtroValor !== undefined
        ? `&filtroCampo=${filtroCampo}&filtroValor=${filtroValor}`
        : "";
      const res = await fetch(
        `/api/catalogos/${tabla}?page=${page}&limit=${limit}&search=${search}&estatus=${estatusFiltro}${filtroExtra}`,
        { credentials: "include", headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // EXPORTAR CSV
  // =========================
  const exportarCSV = async () => {
    if (!cfg) return;
    setExportando(true);
    try {
      const res = await fetch(
        `/api/catalogos/${tabla}?page=1&limit=10000&search=${search}&estatus=${estatusFiltro}`,
        { credentials: "include", headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await res.json();
      const filas = result.data || [];

      const encabezados = cfg.columnas.map((c) => c.label);
      const lineas = [encabezados.join(",")];

      filas.forEach((row) => {
        const valores = cfg.columnas.map((c) => {
          const valor = renderValor(c, row[c.campo]);
          const texto = typeof valor === "string" || typeof valor === "number" ? valor : row[c.campo];
          return `"${String(texto ?? "").replace(/"/g, '""')}"`;
        });
        lineas.push(valores.join(","));
      });

      const csv = "﻿" + lineas.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tabla}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exportando:", error);
    } finally {
      setExportando(false);
    }
  };

  const cargarReferencias = async () => {
    if (!cfg) return;
    const refs = {};
    for (const col of cfg.columnas) {
      if (col.tipo === "select" && col.ref) {
        try {
          const res = await fetch(
            `/api/catalogos/${col.ref}?limit=1000`,
            { credentials: "include", headers: { Authorization: `Bearer ${token}` } }
          );
          const data = await res.json();
          refs[col.ref] = data.data || [];
        } catch (error) {
          console.error("Error cargando referencia:", col.ref);
        }
      }
    }
    setRefsData(refs);
  };

  useEffect(() => {
    if (cfg) {
      cargarDatos();
      cargarReferencias();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabla, page, search, estatusFiltro]);

  // =========================
  // CRUD
  // =========================
  // row = registro que se edita (para que su propio miembro / servicio siga en la lista de opciones).
  const cargarOpcionesCampo = async (row) => {
    for (const col of cfg.columnas) {
      if (!col.opcionesEndpoint) continue;
      try {
        const params = new URLSearchParams();
        if (row?.id_miembro) params.set("excluir_id_miembro", row.id_miembro);
        if (row?.id_servicio) params.set("incluir_id_servicio", row.id_servicio);
        const url = params.toString() ? `${col.opcionesEndpoint}?${params.toString()}` : col.opcionesEndpoint;
        const res = await fetch(url, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setOpcionesCampo((prev) => ({ ...prev, [col.campo]: data || [] }));
      } catch (error) {
        console.error("Error cargando opciones de", col.campo, error);
      }
    }
  };

  const abrirNuevo = () => {
    const tieneActivo = cfg.columnas.some((c) => c.campo === "activo");
    // Valores iniciales opcionales por columna (valorInicial en la configuración del catálogo).
    const iniciales = Object.fromEntries(cfg.columnas.filter((c) => c.valorInicial !== undefined).map((c) => [c.campo, c.valorInicial]));
    setFormData({ ...(tieneActivo ? { activo: true } : {}), ...iniciales, ...valoresFijos });
    setEditando(false);
    setShowModal(true);
    cargarOpcionesCampo();
  };

  const abrirEditar = (row) => {
    const nuevoForm = { ...row };

    // FORMATEAR FECHA Y HORA PARA INPUT
    cfg.columnas.forEach((col) => {
      if (col.tipo === "date" && row[col.campo]) {
        const d = new Date(row[col.campo]);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        nuevoForm[col.campo] = `${yyyy}-${mm}-${dd}`;
      }
      if (col.tipo === "time" && row[col.campo]) {
        const t = new Date(`1970-01-01T${row[col.campo]}`);
        const hh = String(t.getHours()).padStart(2, "0");
        const mi = String(t.getMinutes()).padStart(2, "0");
        nuevoForm[col.campo] = `${hh}:${mi}`;
      }
    });

    setFormData(nuevoForm);
    setEditando(true);
    setShowModal(true);
    cargarOpcionesCampo(row);
  };

  const guardar = async () => {
    const method = editando ? "PUT" : "POST";
    const url = editando
      ? `/api/catalogos/${tabla}/${formData[cfg.id]}`
      : `/api/catalogos/${tabla}`;

    // Solo se envían los campos editables definidos en el catálogo: al editar,
    // formData parte de la fila completa (incluye columnas de otras tablas
    // traídas por JOIN solo para mostrarse, como el nombre del miembro o una
    // fecha resuelta desde otra tabla), y mandarlas de vuelta rompía el UPDATE
    // porque esas columnas no existen (o no son editables) en la tabla destino.
    const camposValidos = new Set(
      cfg.columnas.filter((c) => c.editable !== false || c.campo in valoresFijos).map((c) => c.campo)
    );
    const payload = Object.fromEntries(
      Object.entries(formData).filter(([key]) => camposValidos.has(key))
    );

    try {
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // Si el servidor explica el motivo (duplicado, valor no permitido) se muestra tal cual.
        const cuerpo = res.status === 409 || res.status === 400 ? await res.json().catch(() => ({})) : {};
        mostrarToast(cuerpo.error || "Error guardando el registro", "error");
        return;
      }

      setShowModal(false);
      mostrarToast(editando ? "Registro actualizado" : "Registro creado");
      cargarDatos();
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    }
  };

  // =========================
  // DESACTIVAR / REACTIVAR
  // =========================
  const abrirConfirm = (id, tipo) => {
    setSelectedId(id);
    setAccionTipo(tipo);
    setConfirmOpen(true);
  };

  const ejecutarAccion = async () => {
    try {
      await fetch(
        `/api/catalogos/${tabla}/${selectedId}`,
        {
          method: accionTipo === "desactivar" ? "DELETE" : "PATCH",
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setConfirmOpen(false);
      setSelectedId(null);
      cargarDatos();
    } catch (error) {
      console.error(error);
    }
  };

  // =========================
  // RENDER TABLA
  // =========================
  const renderValor = (col, value) => {
    if (col.tipo === "select" && col.opciones) {
      const encontrado = col.opciones.find((o) => String(o.value) === String(value));
      return encontrado ? encontrado.label : value;
    }

    if (col.tipo === "select" && col.ref) {
      const lista = refsData[col.ref] || [];
      const encontrado = lista.find((item) => item[col.refId] === value);
      if (!encontrado) return value;
      return Array.isArray(col.refLabel)
        ? col.refLabel.map((k) => encontrado[k]).join(" ")
        : encontrado[col.refLabel];
    }

    if (col.tipo === "checkbox") {
      return value ? (
        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium">
          Activo
        </span>
      ) : (
        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-medium">
          Inactivo
        </span>
      );
    }

    if (col.tipo === "date" && value) {
      return new Date(value).toLocaleDateString("es-MX", { timeZone: "UTC" });
    }

    if (col.tipo === "time" && value) {
      return value;
    }

    return value || "-";
  };

  if (!cfg)
    return (
      <div className="p-6 text-center text-rose-500">
        Configuración no encontrada
      </div>
    );

  const columnasVisibles = cfg.columnas.filter((c) => c.campo !== "activo" && c.mostrarEnTabla !== false);
  const inicioRango = total === 0 ? 0 : (page - 1) * limit + 1;
  const finRango = Math.min(page * limit, total);

  return (
    <div className="p-6">

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600">Administración</Link>
        <span className="mx-1.5">/</span>
        {onBack && (
          <>
            <button onClick={onBack} className="hover:text-indigo-600">{nombreSeccion}</button>
            <span className="mx-1.5">/</span>
          </>
        )}
        <span className="text-slate-600 font-medium">{nombre}</span>
      </div>

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{nombre}</h1>
          {descripcion && <p className="text-sm text-slate-500 mt-0.5">{descripcion}</p>}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportarCSV}
            disabled={exportando}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
          >
            <FaDownload className="text-xs" /> {exportando ? "Exportando..." : "Exportar"}
          </button>

          <button
            onClick={abrirNuevo}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            <FaPlus className="text-xs" /> Nuevo {nombreSingular}
          </button>
        </div>
      </div>

      {/* BARRA DE FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder={cfg.placeholderBusqueda || "Buscar por nombre..."}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => {
              setEstatusFiltro(e.target.value);
              setPage(1);
            }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {columnasVisibles.map((col, idx) => (
                  <th key={col.campo} className="px-4 py-3 text-left font-semibold">
                    {idx === 0 ? nombreSingular : col.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-left font-semibold">Estatus</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnasVisibles.length + 2} className="text-center py-10 text-slate-400">
                    Cargando...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((row) => (
                  <tr
                    key={row[cfg.id]}
                    className="border-t border-slate-100 hover:bg-slate-50 transition"
                  >
                    {columnasVisibles.map((col, idx) => (
                      <td key={col.campo} className="px-4 py-3 text-slate-700">
                        {idx === 0 ? (
                          <span className="flex items-center gap-2 font-medium text-slate-800">
                            {Icono && (
                              <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                                <Icono className="text-xs" />
                              </span>
                            )}
                            {renderValor(col, row[col.campo])}
                          </span>
                        ) : (
                          renderValor(col, row[col.campo])
                        )}
                      </td>
                    ))}

                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className={`w-2 h-2 rounded-full ${row.activo ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {row.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => abrirEditar(row)}
                          title="Editar"
                          className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                        >
                          <FaPencilAlt className="text-xs" />
                        </button>

                        {row.activo ? (
                          <button
                            onClick={() => abrirConfirm(row[cfg.id], "desactivar")}
                            title="Desactivar"
                            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                          >
                            <FaBan className="text-xs" />
                          </button>
                        ) : (
                          <button
                            onClick={() => abrirConfirm(row[cfg.id], "reactivar")}
                            title="Reactivar"
                            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition"
                          >
                            <FaCheck className="text-xs" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columnasVisibles.length + 2} className="text-center py-10 text-slate-400">
                    Sin resultados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINACIÓN */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-slate-500">
          {total === 0 ? "Sin resultados" : `Mostrando ${inicioRango} a ${finRango} de ${total} resultados`}
        </span>

        <div className="flex items-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronLeft className="text-xs" />
          </button>

          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-indigo-600 text-white text-sm font-semibold">
            {page}
          </span>

          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>

      {/* MODAL NUEVO / EDITAR */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editando ? `Editar ${nombreSingular}` : `Nuevo ${nombreSingular}`}
      >
        <div>
            <div className="space-y-3">
              {cfg.columnas.filter((col) => col.campo !== "activo" && col.editable !== false).map((col) => (
                <div key={col.campo}>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    {col.label} <span className="text-rose-500">*</span>
                  </label>

                  {col.tipo === "select" && col.opciones ? (
                    <select
                      value={formData[col.campo] ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [col.campo]: e.target.value,
                        })
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    >
                      <option value="">-- Selecciona --</option>
                      {col.opciones.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  ) : col.tipo === "select" && col.ref ? (
                    <select
                      value={formData[col.campo] || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [col.campo]: e.target.value,
                        })
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    >
                      <option value="">-- Selecciona --</option>
                      {(col.opcionesEndpoint ? opcionesCampo[col.campo] || [] : refsData[col.ref] || []).map((item) => (
                        <option key={item[col.refId]} value={item[col.refId]}>
                          {Array.isArray(col.refLabel)
                            ? col.refLabel.map((k) => item[k]).join(" ")
                            : item[col.refLabel]}
                        </option>
                      ))}
                    </select>
                  ) : col.tipo === "date" ? (
                    <input
                      type="date"
                      value={formData[col.campo] || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, [col.campo]: e.target.value })
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    />
                  ) : col.tipo === "time" ? (
                    <input
                      type="time"
                      value={formData[col.campo] || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, [col.campo]: e.target.value })
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    />
                  ) : col.tipo === "number" ? (
                    <input
                      type="number"
                      value={formData[col.campo] ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [col.campo]: e.target.value === "" ? null : e.target.value,
                        })
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData[col.campo] || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, [col.campo]: e.target.value })
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
              <button
                onClick={() => setShowModal(false)}
                className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
              >
                Cancelar
              </button>

              <button
                onClick={guardar}
                className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition"
              >
                Guardar
              </button>
            </div>
        </div>
      </Modal>

      {/* MODAL CONFIRMAR DESACTIVAR / REACTIVAR */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
            accionTipo === "desactivar" ? "bg-rose-50 text-rose-500" : "bg-emerald-50 text-emerald-500"
          }`}>
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            {accionTipo === "desactivar" ? "Desactivar" : "Reactivar"} registro
          </h3>
          <p className="text-sm text-slate-500 mb-6">¿Deseas continuar?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmOpen(false)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={ejecutarAccion}
              className={`w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition ${
                accionTipo === "desactivar" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}